import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { finalize } from 'rxjs';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { FleetDocument, FleetDocumentSearchParams, FleetDocumentStatus, FleetDocumentType } from '../fleet-documents.model';
import { FleetDocumentsService } from '../fleet-documents.service';

@Component({
  selector: 'app-fleet-documents',
  standalone: true,
  templateUrl: './fleet-documents.component.html',
  styleUrls: ['./fleet-documents.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ToastModule,
    ButtonModule,
    DatePickerModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TagModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService]
})
export class FleetDocumentsComponent implements OnInit {
  isLoading = false;
  isSaving = false;
  documents: FleetDocument[] = [];
  selected?: FleetDocument;

  filters: FleetDocumentSearchParams = {
    resourceType: '',
    documentType: '',
    status: '',
    resourceId: undefined as number | undefined
  };

  form: FleetDocument = this.blankDocument();

  readonly resourceTypeOptions = [
    { label: 'All resources', value: '' },
    { label: 'Driver', value: 'DRIVER' },
    { label: 'Vehicle', value: 'VEHICLE' },
    { label: 'Trailer', value: 'TRAILER' },
    { label: 'Rental Driver', value: 'RENTAL_DRIVER' },
    { label: 'Rental Vehicle', value: 'RENTAL_VEHICLE' },
    { label: 'Rental Agreement', value: 'RENTAL_AGREEMENT' }
  ];

  readonly documentTypeOptions = [
    { label: 'All document types', value: '' },
    { label: 'Iqama / ID', value: 'IQAMA_OR_ID' },
    { label: 'Driver License', value: 'DRIVER_LICENSE' },
    { label: 'MOT License', value: 'MOT_LICENSE' },
    { label: 'Vehicle Registration', value: 'VEHICLE_REGISTRATION' },
    { label: 'Insurance', value: 'INSURANCE' },
    { label: 'Rental Contract', value: 'RENTAL_CONTRACT' },
    { label: 'Inspection Certificate', value: 'INSPECTION_CERTIFICATE' },
    { label: 'Operating Card', value: 'OPERATING_CARD' },
    { label: 'Other', value: 'OTHER' }
  ];

  readonly statusOptions = [
    { label: 'All statuses', value: '' },
    { label: 'Missing', value: 'MISSING' },
    { label: 'Valid', value: 'VALID' },
    { label: 'Due Soon', value: 'DUE_SOON' },
    { label: 'Expired', value: 'EXPIRED' }
  ];

  get metrics(): PageMetric[] {
    const dueSoon = this.countByStatus('DUE_SOON');
    const expired = this.countByStatus('EXPIRED');
    const missing = this.countByStatus('MISSING');
    return [
      { label: 'Documents', value: this.documents.length, hint: 'Current filter', tone: 'info' },
      { label: 'Due Soon', value: dueSoon, hint: 'Within warning window', tone: dueSoon ? 'warn' : 'success' },
      { label: 'Expired', value: expired, hint: 'Needs renewal', tone: expired ? 'danger' : 'success' },
      { label: 'Missing', value: missing, hint: 'Required proof missing', tone: missing ? 'danger' : 'success' }
    ];
  }

  constructor(
    private fleetDocumentsService: FleetDocumentsService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading = true;
    this.fleetDocumentsService.search(this.filters)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: result => this.documents = result.items ?? [],
        error: () => this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Unable to load fleet documents.' })
      });
  }

  save(): void {
    if (!this.form.resourceType || !this.form.resourceId || !this.form.documentType) {
      this.messageService.add({ severity: 'warn', summary: 'Required fields', detail: 'Resource type, resource ID, and document type are required.' });
      return;
    }
    this.isSaving = true;
    const request = this.form.documentId
      ? this.fleetDocumentsService.update(this.form.documentId, this.form)
      : this.fleetDocumentsService.create(this.form);
    request.pipe(finalize(() => this.isSaving = false)).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Fleet document saved.' });
        this.resetForm();
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Save failed', detail: 'Unable to save fleet document.' })
    });
  }

  edit(document: FleetDocument): void {
    this.selected = document;
    this.form = { ...document, expiryDate: this.toDate(document.expiryDate), issuedAt: this.toDate(document.issuedAt) };
  }

  resetForm(): void {
    this.selected = undefined;
    this.form = this.blankDocument();
  }

  evaluate(): void {
    this.isLoading = true;
    this.fleetDocumentsService.evaluateCurrentTenant()
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: () => {
          this.messageService.add({ severity: 'success', summary: 'Evaluated', detail: 'Compliance statuses were refreshed.' });
          this.load();
        },
        error: () => this.messageService.add({ severity: 'error', summary: 'Evaluation failed', detail: 'Unable to refresh compliance statuses.' })
      });
  }

  severity(status?: string): 'success' | 'warn' | 'danger' | 'secondary' | 'info' {
    switch (status) {
      case 'VALID': return 'success';
      case 'DUE_SOON': return 'warn';
      case 'EXPIRED':
      case 'MISSING': return 'danger';
      default: return 'secondary';
    }
  }

  display(value?: string): string {
    return value ? value.replace(/_/g, ' ') : '-';
  }

  private countByStatus(status: FleetDocumentStatus): number {
    return this.documents.filter(document => document.status === status).length;
  }

  private blankDocument(): FleetDocument {
    return {
      resourceType: 'DRIVER',
      documentType: 'DRIVER_LICENSE' as FleetDocumentType,
      required: true,
      alertLeadDays: 30
    };
  }

  private toDate(value?: string | Date): Date | undefined {
    if (!value) {
      return undefined;
    }
    return value instanceof Date ? value : new Date(value);
  }
}
